import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import Reveal from '../components/common/Reveal';
import { getBreadcrumbSchema } from '../utils/seoSchemas';
import { useContentBlocks } from '../hooks/useContentBlock';
import ContentBlockPlaceholder from '../components/content/ContentBlockPlaceholder';

/* ─────────── Editorial defaults (used when no admin block is authored) ───────── */
const FALLBACK_HERO_IMG = 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1800&q=80';
const FALLBACK_BIO_IMG = 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=1200&q=80';
const FALLBACK_BIO_INSET = 'https://images.unsplash.com/photo-1604176354204-9268737828e4?w=900&q=80';
const FALLBACK_HISTORIA_IMG = 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1200&q=80';

/**
 * Oversized decorative italic glyph, used behind hero + numbered sections.
 */
const BigGhostNumber = ({ children, top, right, left, bottom, size = '32vw', opacity = 0.08 }) => (
  <span
    aria-hidden="true"
    style={{
      position: 'absolute',
      top,
      right,
      left,
      bottom,
      fontFamily: 'var(--font-display)',
      fontStyle: 'italic',
      fontSize: size,
      lineHeight: 0.85,
      color: 'var(--terracotta)',
      opacity,
      pointerEvents: 'none',
      userSelect: 'none',
      fontVariationSettings: "'opsz' 144, 'SOFT' 100",
    }}
  >
    {children}
  </span>
);

/**
 * Render HTML body from an admin block. `dangerouslySetInnerHTML` is safe
 * because the backend pipes every section body through bleach on POST/PUT.
 */
const HtmlBody = ({ html, style }) => (
  <div
    style={{ fontSize: '17px', lineHeight: 1.75, color: 'var(--ink-soft)', ...style }}
    dangerouslySetInnerHTML={{ __html: html || '' }}
  />
);

const AboutPage = () => {
  const { t } = useTranslation();
  const blocks = useContentBlocks('about-us');
  const b = (key) => blocks[key] || null;

  // ---- Slot resolution with fallbacks (defaults preserve the original page look) ----
  const hero = b('about.hero.header');
  const heroIntro = b('about.hero.intro');
  const heroPortrait = b('about.hero.portrait');
  const heroImage = heroPortrait?.images?.[0]?.url || FALLBACK_HERO_IMG;
  // Use `??` so an admin who deliberately clears the caption gets an empty
  // string (falls back to defaults only when the field is missing).
  const heroCaption = heroPortrait?.images?.[0]?.caption ?? t('about.heroCaption');

  const manifesto = b('about.manifesto.header');
  const manifestoBody = b('about.manifesto.body');

  const queHacemos = b('about.quehacemos.header');
  const queHacemosBody = b('about.quehacemos.body');
  const queHacemosGallery = b('about.quehacemos.gallery');
  const bioImage = queHacemosGallery?.images?.[0]?.url || FALLBACK_BIO_IMG;
  const bioInsetImage = queHacemosGallery?.images?.[1]?.url || FALLBACK_BIO_INSET;

  const historia = b('about.historia.header');
  const historiaBody = b('about.historia.body');
  const historiaImg = b('about.historia.image');
  const historiaImage = historiaImg?.images?.[0]?.url || FALLBACK_HISTORIA_IMG;
  const historiaCaption =
    historiaImg?.images?.[0]?.caption ?? t('about.historiaCaption');

  const stats = [1, 2, 3, 4]
    .map((n) => b(`about.stats.${n}`))
    .filter(Boolean)
    .map((blk) => ({ number: blk.heading, label: blk.subheading }));

  const pillarsHeader = b('about.pillars.header');
  const mision = b('about.pillars.mision');
  const vision = b('about.pillars.vision');

  const quote = b('about.quote.main');
  const cta = b('about.cta.explore');

  const title = hero?.heading?.split(' · ')[0] || t('about.title');
  const heroSubtitle = hero?.subheading || t('about.heroSubtitle');
  const heroIntroHtml = heroIntro?.body || '';

  const breadcrumbs = [
    { name: t('about.breadcrumbHome'), url: '/' },
    { name: title, url: '/page/about-us' },
  ];

  return (
    <>
      <SEOHead
        title={title}
        description={heroIntroHtml.replace(/<[^>]*>/g, '').slice(0, 160)}
        url="/page/about-us"
        type="website"
      />
      <script type="application/ld+json">{JSON.stringify(getBreadcrumbSchema(breadcrumbs))}</script>

      <div style={{ background: 'var(--bg)', overflow: 'hidden' }} data-testid="about-page-editorial">
        {/* ═════════════ HERO ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.hero.*">
          <section
            data-testid="about-hero"
            className="about-hero-section"
            style={{
              position: 'relative',
              padding: '160px 40px 120px',
              overflow: 'hidden',
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <BigGhostNumber top="6vw" right="-3vw" size="52vw" opacity={0.06}>✦</BigGhostNumber>
            <div
              className="container mx-auto about-hero-grid"
              style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1.25fr 1fr',
                gap: '80px',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Reveal stagger y={32}>
                <span
                  className="eyebrow"
                  style={{ color: 'var(--ink-soft)', display: 'inline-flex', alignItems: 'center', gap: '12px' }}
                >
                  <span style={{ display: 'inline-block', width: '36px', height: '1px', background: 'var(--terracotta)' }} />
                  {hero?.heading || t('about.heroEyebrow', { title, interpolation: { escapeValue: false } })}
                </span>
                <h1
                  className="about-hero-title"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(56px, 8.5vw, 140px)',
                    fontWeight: 300,
                    lineHeight: 0.92,
                    letterSpacing: '-3px',
                    marginTop: '32px',
                    marginBottom: '40px',
                    color: 'var(--ink)',
                  }}
                  dangerouslySetInnerHTML={{ __html: heroSubtitle }}
                />
                {heroIntroHtml && (
                  <HtmlBody html={heroIntroHtml} style={{ maxWidth: '520px', marginBottom: '32px' }} />
                )}
              </Reveal>

              <Reveal y={48}>
                <div style={{ position: 'relative', width: '100%', maxWidth: '480px', marginLeft: 'auto' }}>
                  <img
                    src={heroImage}
                    alt={heroCaption}
                    loading="eager"
                    decoding="async"
                    style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block' }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '20px',
                      right: '-12px',
                      transform: 'rotate(90deg)',
                      transformOrigin: 'right top',
                      fontFamily: 'var(--font-body)',
                      fontSize: '10px',
                      letterSpacing: '4px',
                      textTransform: 'uppercase',
                      color: 'var(--ink-soft)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {heroCaption}
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        </ContentBlockPlaceholder>

        {/* ═════════════ MANIFIESTO ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.manifesto.*">
          <section
            data-testid="about-intro"
            style={{ position: 'relative', padding: '180px 40px', textAlign: 'center', overflow: 'hidden' }}
          >
            <BigGhostNumber top="-2vw" left="50%" size="44vw" opacity={0.045}>✦</BigGhostNumber>
            <Reveal stagger style={{ maxWidth: '860px', margin: '0 auto', position: 'relative' }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: '40px' }}>
                {manifesto?.heading || t('about.manifestoHeading')}
              </span>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(40px, 6vw, 80px)',
                  fontWeight: 400,
                  lineHeight: 1.05,
                  letterSpacing: '-1.5px',
                  color: 'var(--ink)',
                  marginBottom: '40px',
                }}
                dangerouslySetInnerHTML={{
                  __html: manifesto?.subheading || t('about.manifestoSubheading'),
                }}
              />
              <HtmlBody html={manifestoBody?.body || ''} style={{ fontSize: '18px' }} />
            </Reveal>
          </section>
        </ContentBlockPlaceholder>

        {/* ═════════════ QUÉ HACEMOS ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.quehacemos.*">
          <section
            data-testid="about-que-hacemos"
            style={{ position: 'relative', padding: '120px 40px 180px', background: 'var(--bg-alt)', overflow: 'hidden' }}
          >
            <BigGhostNumber top="80px" right="-2vw" size="32vw">01</BigGhostNumber>
            <div
              className="container mx-auto about-bio-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '1.1fr 1fr',
                gap: '80px',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Reveal>
                <div style={{ position: 'relative', minHeight: '600px' }}>
                  <img
                    src={bioImage}
                    alt={queHacemos?.heading || t('about.queHacemosHeading')}
                    loading="lazy"
                    decoding="async"
                    style={{ width: '78%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block' }}
                  />
                  <img
                    src={bioInsetImage}
                    alt={t('about.detailAlt')}
                    loading="lazy"
                    decoding="async"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '45%',
                      aspectRatio: '3 / 4',
                      objectFit: 'cover',
                      border: '12px solid var(--bg-alt)',
                    }}
                  />
                </div>
              </Reveal>

              <Reveal stagger y={24}>
                <span className="eyebrow" style={{ display: 'block', marginBottom: '24px' }}>
                  {queHacemos?.heading || t('about.queHacemosHeading')}
                </span>
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(36px, 4.5vw, 64px)',
                    fontWeight: 400,
                    lineHeight: 1.02,
                    letterSpacing: '-1.5px',
                    color: 'var(--ink)',
                    marginBottom: '32px',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: queHacemos?.subheading || t('about.queHacemosSubheading'),
                  }}
                />
                <HtmlBody html={queHacemosBody?.body || ''} />
              </Reveal>
            </div>
          </section>
        </ContentBlockPlaceholder>

        {/* ═════════════ HISTORIA ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.historia.*">
          <section
            data-testid="about-historia"
            style={{ position: 'relative', padding: '160px 40px', overflow: 'hidden' }}
          >
            <BigGhostNumber top="60px" left="-1vw" size="34vw" opacity={0.05}>02</BigGhostNumber>
            <div
              className="container mx-auto about-historia-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '0.85fr 1.15fr',
                gap: '90px',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Reveal>
                <figure style={{ margin: 0, position: 'relative' }} data-testid="about-historia-image">
                  <img
                    src={historiaImage}
                    alt={historiaCaption}
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block' }}
                  />
                  <figcaption
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontStyle: 'italic',
                      fontVariationSettings: "'opsz' 60, 'SOFT' 100",
                      fontSize: '14px',
                      color: 'var(--ink-soft)',
                      marginTop: '14px',
                      letterSpacing: '0.2px',
                    }}
                  >
                    {historiaCaption}
                  </figcaption>
                </figure>
              </Reveal>

              <Reveal stagger y={24}>
                <span
                  className="eyebrow"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}
                >
                  <span style={{ display: 'inline-block', width: '28px', height: '1px', background: 'var(--terracotta)' }} />
                  {historia?.heading || t('about.historiaHeading')}
                </span>
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(34px, 4vw, 56px)',
                    fontWeight: 400,
                    lineHeight: 1.05,
                    letterSpacing: '-1.2px',
                    color: 'var(--ink)',
                    marginBottom: '28px',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: historia?.subheading || t('about.historiaSubheading'),
                  }}
                />
                <HtmlBody html={historiaBody?.body || ''} style={{ fontSize: '15.5px' }} />
              </Reveal>
            </div>
          </section>
        </ContentBlockPlaceholder>

        {/* ═════════════ STATS ═════════════ */}
        {stats.length > 0 && (
          <ContentBlockPlaceholder blockKey="about.stats.*">
            <section
              data-testid="about-stats"
              style={{
                background: 'var(--bg-dark)',
                color: '#f5f0e8',
                padding: '100px 40px',
                borderTop: '1px solid rgba(255,255,255,0.05)',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Reveal stagger y={24}>
                <div
                  className="container mx-auto about-stats-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
                    gap: '60px',
                  }}
                >
                  {stats.map((s, i) => (
                    <div key={i} style={{ textAlign: 'left' }}>
                      <div
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'clamp(56px, 7vw, 110px)',
                          fontWeight: 300,
                          lineHeight: 0.95,
                          letterSpacing: '-3px',
                          color: '#fefcf7',
                          marginBottom: '12px',
                        }}
                      >
                        {s.number}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '3px',
                          color: 'var(--terracotta)',
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </section>
          </ContentBlockPlaceholder>
        )}

        {/* ═════════════ MISIÓN + VISIÓN ═════════════ */}
        {(mision || vision) && (
          <ContentBlockPlaceholder blockKey="about.pillars.*">
            <section
              data-testid="about-vision-mision"
              style={{ position: 'relative', padding: '180px 40px', overflow: 'hidden' }}
            >
              <BigGhostNumber bottom="40px" right="-1vw" size="32vw" opacity={0.05}>03</BigGhostNumber>
              <div className="container mx-auto" style={{ position: 'relative' }}>
                <Reveal stagger>
                  <div style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 100px' }}>
                    <span className="eyebrow" style={{ display: 'block', marginBottom: '32px' }}>
                      {pillarsHeader?.heading || t('about.pillarsHeading')}
                    </span>
                    <h2
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'clamp(40px, 5vw, 72px)',
                        fontWeight: 400,
                        lineHeight: 1.05,
                        letterSpacing: '-1.5px',
                        color: 'var(--ink)',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: pillarsHeader?.subheading || t('about.pillarsSubheading'),
                      }}
                    />
                  </div>
                </Reveal>
                <Reveal stagger y={24}>
                  <div
                    className="about-pillars-grid"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '80px' }}
                  >
                    {[
                      { block: mision, num: '01', testid: 'about-mision' },
                      { block: vision, num: '02', testid: 'about-vision' },
                    ]
                      .filter((x) => x.block)
                      .map(({ block, num, testid }) => (
                        <div
                          key={testid}
                          style={{ borderTop: '1px solid var(--line)', paddingTop: '40px' }}
                          data-testid={testid}
                        >
                          <div
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontStyle: 'italic',
                              fontVariationSettings: "'opsz' 60, 'SOFT' 100",
                              fontSize: '20px',
                              color: 'var(--terracotta)',
                              marginBottom: '20px',
                              letterSpacing: '1px',
                            }}
                          >
                            {num}
                          </div>
                          <h3
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: '40px',
                              fontWeight: 400,
                              lineHeight: 1.05,
                              color: 'var(--ink)',
                              marginBottom: '20px',
                              letterSpacing: '-0.8px',
                            }}
                          >
                            {block.heading}
                          </h3>
                          <HtmlBody html={block.body || ''} style={{ fontSize: '15.5px' }} />
                        </div>
                      ))}
                  </div>
                </Reveal>
              </div>
            </section>
          </ContentBlockPlaceholder>
        )}

        {/* ═════════════ QUOTE ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.quote.main">
          <section
            data-testid="about-quote"
            style={{
              background: 'var(--bg-dark)',
              color: '#f5f0e8',
              padding: '180px 40px',
              textAlign: 'center',
            }}
          >
            <Reveal stagger y={24}>
              <div
                aria-hidden="true"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  fontVariationSettings: "'opsz' 144, 'SOFT' 100",
                  fontSize: '240px',
                  lineHeight: 0.4,
                  color: 'var(--terracotta)',
                  marginBottom: '32px',
                }}
              >
                “
              </div>
              <blockquote
                style={{
                  fontFamily: 'var(--font-display)',
                  fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                  fontSize: 'clamp(36px, 5.5vw, 80px)',
                  fontWeight: 300,
                  lineHeight: 1.05,
                  letterSpacing: '-1.5px',
                  maxWidth: '1000px',
                  margin: '0 auto 40px',
                }}
                dangerouslySetInnerHTML={{
                  __html: quote?.body || t('about.quote'),
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  fontVariationSettings: "'opsz' 144, 'SOFT' 100",
                  fontSize: '240px',
                  lineHeight: 0.4,
                  color: 'var(--terracotta)',
                  marginBottom: '32px',
                }}
              >
                ”
              </div>
              <cite
                style={{
                  fontStyle: 'normal',
                  fontFamily: 'var(--font-body)',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '4px',
                  color: 'var(--terracotta)',
                }}
              >
                {quote?.caption || t('about.quoteCite', { title, interpolation: { escapeValue: false } })}
              </cite>
            </Reveal>
          </section>
        </ContentBlockPlaceholder>

        {/* ═════════════ CTA ═════════════ */}
        <ContentBlockPlaceholder blockKey="about.cta.explore">
          <section
            data-testid="about-cta"
            style={{ position: 'relative', padding: '180px 40px', textAlign: 'center', overflow: 'hidden' }}
          >
            <BigGhostNumber top="-3vw" left="50%" size="46vw" opacity={0.04}>✦</BigGhostNumber>
            <Reveal stagger style={{ position: 'relative' }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: '24px' }}>
                {cta?.heading || t('about.ctaHeading')}
              </span>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(40px, 6vw, 80px)',
                  fontWeight: 400,
                  lineHeight: 1.02,
                  letterSpacing: '-1.5px',
                  color: 'var(--ink)',
                  marginBottom: '32px',
                }}
                dangerouslySetInnerHTML={{
                  __html: cta?.subheading || t('about.ctaSubheading'),
                }}
              />
              <HtmlBody
                html={cta?.body || t('about.ctaBody')}
                style={{ maxWidth: '560px', margin: '0 auto 48px' }}
              />
              <Link to={cta?.ctaUrl || '/shop'} className="btn-primary" data-testid="about-cta-btn">
                {cta?.ctaLabel || t('about.ctaLabel')}
              </Link>
            </Reveal>
          </section>
        </ContentBlockPlaceholder>

        {/* Mobile responsive collapses */}
        <style>{`
          @media (max-width: 900px) {
            .about-hero-section { padding-top: 120px !important; padding-bottom: 80px !important; min-height: auto !important; }
            .about-hero-grid { grid-template-columns: 1fr !important; gap: 60px !important; }
            .about-bio-grid,
            .about-historia-grid { grid-template-columns: 1fr !important; gap: 60px !important; }
            .about-pillars-grid { grid-template-columns: 1fr !important; gap: 50px !important; }
            .about-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 40px !important; }
          }
        `}</style>
      </div>
    </>
  );
};

export default AboutPage;
